"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.chargeReportQuerySchema = exports.chargeReportExportParamsSchema = exports.chargeReportParamsSchema = exports.rejectChargePriceVersionSchema = exports.resolveRatesRequestSchema = exports.rateResolutionInputSchema = exports.encounterContextSchema = exports.createChargePriceVersionSchema = exports.chargeConditionInputSchema = exports.chargeConditionOperatorSchema = exports.chargeConditionAttributeSchema = exports.chargeRateTypeSchema = void 0;
/**
 * Enterprise Charge Master, Phase 3 — price versioning + rate resolution
 * contracts. See `docs/ENTERPRISE-CHARGE-MASTER-ARCHITECTURE.md` §3.2/§5.
 */
const zod_1 = require("zod");
/** ADR-044 — a closed set. The resolver branches on this, so an unknown value could never apply. */
exports.chargeRateTypeSchema = zod_1.z.enum([
    'STANDARD',
    'MRP',
    'HOSPITAL',
    'CASH',
    'CREDIT',
    'EMERGENCY',
    'NIGHT',
    'HOLIDAY',
    'VIP',
    'STAFF',
    'EMPLOYEE',
    'CORPORATE',
    'INSURANCE',
    'GOVERNMENT',
    'CHARITY',
    'PACKAGE',
    'PROMOTIONAL',
    'ZERO',
]);
exports.chargeConditionAttributeSchema = zod_1.z.enum([
    'PATIENT_TYPE',
    'ADMISSION_TYPE',
    'BED_TYPE',
    'DEPARTMENT',
    'DOCTOR',
    'AGE',
    'GENDER',
    'IS_EMERGENCY',
    'IS_HOLIDAY',
    'IS_NIGHT',
    'DAY_OF_WEEK',
    'LOCATION',
]);
exports.chargeConditionOperatorSchema = zod_1.z.enum(['EQUALS', 'NOT_EQUALS', 'IN', 'GREATER_THAN', 'LESS_THAN', 'BETWEEN']);
exports.chargeConditionInputSchema = zod_1.z
    .object({
    attribute: exports.chargeConditionAttributeSchema,
    operator: exports.chargeConditionOperatorSchema,
    value: zod_1.z.string().min(1).max(255),
    valueTo: zod_1.z.string().max(255).optional(),
})
    .refine((c) => c.operator !== 'BETWEEN' || Boolean(c.valueTo), {
    message: 'valueTo is required for a BETWEEN condition',
    path: ['valueTo'],
});
exports.createChargePriceVersionSchema = zod_1.z
    .object({
    chargeId: zod_1.z.string().uuid(),
    rateType: exports.chargeRateTypeSchema,
    /** Integer PAISE (ADR-043) — not rupees. Zero is valid; negative is not. */
    price: zod_1.z.number().int().nonnegative(),
    taxRateMasterId: zod_1.z.string().uuid().optional(),
    effectiveFrom: zod_1.z.coerce.date(),
    effectiveTo: zod_1.z.coerce.date().optional(),
    changeReason: zod_1.z.string().max(2000).optional(),
    conditions: zod_1.z.array(exports.chargeConditionInputSchema).optional(),
})
    .refine((v) => !v.effectiveTo || v.effectiveTo >= v.effectiveFrom, {
    message: 'effectiveTo must be on or after effectiveFrom',
    path: ['effectiveTo'],
});
exports.encounterContextSchema = zod_1.z.object({
    patientType: zod_1.z.enum(['OP', 'IP', 'EMERGENCY', 'DAYCARE']).optional(),
    admissionType: zod_1.z.string().optional(),
    bedTypeId: zod_1.z.string().optional(),
    departmentId: zod_1.z.string().optional(),
    doctorId: zod_1.z.string().optional(),
    locationId: zod_1.z.string().optional(),
    age: zod_1.z.number().int().nonnegative().optional(),
    gender: zod_1.z.string().optional(),
    isEmergency: zod_1.z.boolean().optional(),
    isHoliday: zod_1.z.boolean().optional(),
    isNight: zod_1.z.boolean().optional(),
    dayOfWeek: zod_1.z.number().int().min(0).max(6).optional(),
});
exports.rateResolutionInputSchema = zod_1.z.object({
    chargeId: zod_1.z.string().uuid(),
    quantity: zod_1.z.number().int().positive().optional(),
    asOfDate: zod_1.z.coerce.date().optional(),
    payerId: zod_1.z.string().uuid().optional(),
    packageId: zod_1.z.string().uuid().optional(),
    encounterContext: exports.encounterContextSchema.optional(),
});
/** Batch resolution is the first-class shape (architecture §8) — a bill resolves all its lines in one call. */
exports.resolveRatesRequestSchema = zod_1.z.object({
    items: zod_1.z.array(exports.rateResolutionInputSchema).min(1).max(200),
});
/** ECM Phase 5 — rejecting a submitted price requires a reason; a silent rejection is unauditable. */
exports.rejectChargePriceVersionSchema = zod_1.z.object({
    reason: zod_1.z.string().min(1).max(2000),
});
// ---------------------------------------------------------------------------
// ECM Phase 7 — report contracts.
// ---------------------------------------------------------------------------
/** Report ids are registry keys, not UUIDs — a slug pattern, so a typo 404s cleanly instead of reaching the service. */
exports.chargeReportParamsSchema = zod_1.z.object({
    reportId: zod_1.z.string().min(1).max(60).regex(/^[a-z0-9-]+$/, 'reportId must be a lower-case slug'),
});
exports.chargeReportExportParamsSchema = exports.chargeReportParamsSchema.extend({
    format: zod_1.z.enum(['csv', 'xlsx', 'pdf']),
});
/**
 * Shared filter surface for every report. All optional here — a report that
 * genuinely cannot run without a date range declares that in the registry and
 * `ChargeReportService` enforces it, so the rule lives with the report rather
 * than being duplicated into per-report schemas.
 */
exports.chargeReportQuerySchema = zod_1.z.object({
    fromDate: zod_1.z.coerce.date().optional(),
    toDate: zod_1.z.coerce.date().optional(),
    categoryId: zod_1.z.string().uuid().optional(),
    departmentId: zod_1.z.string().uuid().optional(),
    chargeId: zod_1.z.string().uuid().optional(),
    payerId: zod_1.z.string().uuid().optional(),
    /** String, not boolean — this is a query param, and `?activeOnly=false` arrives as text. */
    activeOnly: zod_1.z.enum(['true', 'false']).optional(),
    limit: zod_1.z.coerce.number().int().positive().max(5000).optional(),
});
//# sourceMappingURL=charge-price-version.schema.js.map